import { useCallback, useState } from 'react';
import TipsDialog from '../components/TipsDialog/TipsDialog';

interface TipsPromise {
  submit: (tipAmount: number) => void;
  cancel: () => void;
}

export const useTipDialog = () => {
  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [tipsPromise, setTipsPromise] = useState<TipsPromise | undefined>(undefined);

  const promptForTips = useCallback((promptTips: boolean): Promise<number> => {
    if (!promptTips) {
      return Promise.resolve(0);
    }

    return new Promise((resolve) => {
      let resolved = false;
      const resolveOnce = (value: number) => {
        if (!resolved) {
          resolved = true;
          resolve(value);
        }
      };

      if (!promptTips) {
        resolveOnce(0);
      }

      const cancel = () => {
        setTimeout(() => setShowTipsDialog(false), 500);

        resolveOnce(0);
      };

      const submit = (tipAmount: number) => {
        setTimeout(() => setShowTipsDialog(false), 5000);

        resolveOnce(tipAmount);
      };

      setTipsPromise({ cancel, submit });
      setShowTipsDialog(true);
    });
  }, []);

  return {
    promptForTips,
    showTipsDialog,
    tipsPromise,
  };
};

export const useTipDialogRemake = () => {
  const [, setShowTipsDialog] = useState(false);
  const [tipState, setTipState] = useState<{
    totalPrice: number;
    currency: string;
    resolve: (tip: number) => void;
  } | null>(null);

  const promptForTips = useCallback((promptTips: boolean, totalPrice: number, currency: string): Promise<number> => {
    if (!promptTips) {
      return Promise.resolve(0);
    }

    return new Promise((resolve) => {
      setTipState({ currency, resolve, totalPrice });
      setShowTipsDialog(true);
    });
  }, []);

  const cancelTip = useCallback(() => {
    tipState?.resolve(0);
    setShowTipsDialog(false);
    setTipState(null);
  }, [tipState]);

  const confirmTip = useCallback(
    (tipAmount: number) => {
      tipState?.resolve(tipAmount);
      setShowTipsDialog(false);
      setTipState(null);
    },
    [tipState]
  );

  const TipsDialogUI = tipState ? (
    <TipsDialog
      cancelTip={cancelTip}
      confirmTip={confirmTip}
      totalPrice={tipState.totalPrice}
      currency={tipState.currency}
    />
  ) : null;

  return { TipsDialogUI, promptForTips };
};
